import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('super_admin' | 'provider' | 'customer')[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { profile, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-ink border-t-transparent rounded-full animate-spin" />
        <p className="label-caps text-ink-muted">Verifying identity...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-8 text-center">
        <h1 className="display text-2xl text-ink mb-4">Access restricted</h1>
        <p className="text-ink-muted max-w-md text-sm mb-8">
          Your account is not authorized. Please use a valid invitation link.
        </p>
        <button
          onClick={() => window.location.href = '/'}
          className="px-8 min-h-[48px] bg-inverse text-ink-inverse dark:bg-accent dark:text-accent-contrast rounded-sm label-caps hover:opacity-90 transition-opacity"
        >
          Return to login
        </button>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
